import { useState, useContext, useEffect } from "react";
import MainStateContext from "../../../state/mainStateContext";
import usePhotoModerationUpload from "../../../usePhotoModeration/usePhotoModerationUpload";
import PhotoTypeSelect from "./PhotoTypeSelect";
import PhotoLoad from "./PhotoLoad";
import PhotoBoothReview from "./PhotoBoothReview";
import { triggerAnalyticsEvent, getCheckoutProduct } from "../../../helpers";
import { StateContextType } from "../../../types/state.types";
import SelfieModerationSteps from "./SelfieModerationSteps";
import { getPhrase, Say } from "../../../languages";

const PhotoModerationWrapper = () => {
  const { state, dispatch } = useContext(MainStateContext) as StateContextType;
  const { eventData } = state;
  const { PhotoType }: { PhotoType?: string } = eventData;
  const photoType = PhotoType?.toLowerCase();
  const {
    base64Photo,
    handleFileChange,
    photoModerationError,
    photoModerationLoading,
    photoModResponse,
    photoDenyReasons
  } = usePhotoModerationUpload();
  const [photoModerationStep, setPhotoModerationStep] = useState("select_photo");
  const [isServiceError, setIsServiceError] = useState(false);
  const introTextEnabled =
    photoModerationStep === "select_photo" || photoModerationStep === "selfie_review";

  useEffect(() => {
    if (photoDenyReasons) {
      const rejectReasonsString = photoDenyReasons?.join(", ");

      setIsServiceError(false);

      const errorMessageText = getPhrase("photo_step.photo_reject_message");
      triggerAnalyticsEvent("photo_moderation_error", {
        eventLabel: `${errorMessageText}:${rejectReasonsString}`,
        errorMessage: `${errorMessageText}:${rejectReasonsString}`
      });
    }
  }, [photoDenyReasons]);

  useEffect(() => {
    if (photoModerationError) {
      setIsServiceError(true);
      setPhotoModerationStep("selfie_rejected");
    }
  }, [photoModerationError]);

  const product = getCheckoutProduct({
    eventNumber: state?.eventData?.EventNumber,
    keyword: state?.eventData?.SmsKeyword,
    cobrand: state?.cobrand
  });

  const addChosenProduct = (chosenProductData) => {
    dispatch({ type: "SET_STEP2", payload: { enabled: true } });
    window.dispatchEvent(
      new CustomEvent("wvcheckout:addProducts", {
        detail: {
          products: [chosenProductData]
        }
      })
    );
  };

  const handlePhotoUploadComplete = (data) => {
    if (data?.imageHandshake && data?.imageUrl) {
      setPhotoModerationStep("selfie_review");
      dispatch({ type: "SET_PHOTO_MOD_DATA", payload: data });
    } else if (!data?.imageHandshake && !data?.photoModeration?.accepted) {
      setPhotoModerationStep("selfie_rejected");
    } else {
      // TODO: We Need To Better Handle This Error State - https://worldvision.jira.com/browse/DEV-5578
      setPhotoModerationStep("select_photo");
    }
  };

  const handlePhotoAccept = (event: React.MouseEvent<HTMLButtonElement>) => {
    const eventButtonText = (event.target as HTMLButtonElement).innerText;

    const selfieProduct = {
      ...product,
      meta: {
        ...product.meta,
        wvus_product_event_donor_resources: photoModResponse?.imageUrl,
        wvus_product_event_donor_resources_handshake: photoModResponse?.imageHandshake
      }
    };

    setPhotoModerationStep("selfie_accepted");
    addChosenProduct(selfieProduct);

    triggerAnalyticsEvent("photo_upload_click", {
      chosen_photo_click: "chosen_photo_confirm",
      eventLabel: eventButtonText
    });
  };

  const handlePhotoBoothClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const eventButtonText = (event.target as HTMLButtonElement).innerText;

    const boothProduct = {
      ...product,
      meta: {
        ...product.meta,
        wvus_product_convert_trad_indicator: true
      }
    };

    setPhotoModerationStep("photo_booth_review");
    addChosenProduct(boothProduct);

    triggerAnalyticsEvent("photo_upload_click", {
      chosen_photo_click: "chosen_use_photo_booth",
      eventLabel: eventButtonText
    });
  };

  return (
    <div
      className={`photo-moderation__wrapper ${photoModerationStep === "photo_booth_review" ? "photo-moderation__wrapper--secondary" : ""}`}>
      <p className={`photo-moderation__intro-text eta ${!introTextEnabled && "hidden"}`}>
        <Say phrase="photo_step.step_heading" />
      </p>

      {photoModerationLoading ? (
        <PhotoLoad />
      ) : (
        <>
          <PhotoTypeSelect
            visible={photoModerationStep === "select_photo"}
            handleFileChange={(e) => handleFileChange(e, handlePhotoUploadComplete)}
            handlePhotoBoothClick={handlePhotoBoothClick}
            photoType={photoType}
          />

          <PhotoBoothReview visible={photoModerationStep === "photo_booth_review"} />

          <SelfieModerationSteps
            photoType={photoType}
            base64Photo={base64Photo}
            step={photoModerationStep}
            handlePhotoAccept={handlePhotoAccept}
            handlePhotoBoothClick={handlePhotoBoothClick}
            handleFileChange={(e) => handleFileChange(e, handlePhotoUploadComplete)}
            isServiceError={isServiceError}
          />
        </>
      )}
    </div>
  );
};

export default PhotoModerationWrapper;
